'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import Container from '@/components/@base/container';
import { Text } from '@/components/@base/typography';
import { STEPS } from '@/components/app/product/components/content/description-ready-site/constants';

import imagesList from '@/constant/images-list';
import { pageLevelLocalization } from '@/constant/localization';
const { product: productLocalization } = pageLevelLocalization;

export default function StepTimelineAccordion() {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const hasActivatedRef = useRef(false);
  const [hasShownStepImage, setHasShownStepImage] = useState(false);
  const [visibleStep, setVisibleStep] = useState<number | null>(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    const element = sectionRef.current;
    let isInView = false;

    const tryActivate = () => {
      if (hasActivatedRef.current || !isInView) return;

      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (rect.top <= viewportHeight * 0.4) {
        hasActivatedRef.current = true;
        setActiveStep(1);
        setVisibleStep(1);
        setHasShownStepImage(true);
        window.removeEventListener('scroll', tryActivate);
        observer.disconnect();
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isInView = entry.isIntersecting;
        tryActivate();
      },
      {
        threshold: 0,
      },
    );

    observer.observe(element);
    window.addEventListener('scroll', tryActivate);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', tryActivate);
    };
  }, []);

  const steps = STEPS;

  const contentRefs = useRef<Array<HTMLDivElement | null>>([]);

  const getContentHeight = (index: number) => {
    const ref = contentRefs.current[index];
    return ref ? ref.scrollHeight : 0;
  };

  return (
    <Container
      ref={sectionRef}
      className='mx-2 mt-2 -mb-2 h-[621px] max-w-6xl rounded-lg bg-[linear-gradient(180deg,_#FEF6EB_0%,_#FFFFFF_63.94%,_#FEF9F2_100%)] pt-4 pr-2 pl-2.5 md:-mb-4 md:h-[400px] md:pt-6 md:pr-5 md:pl-7'
    >
      <Container className='flex flex-col items-center justify-between gap-9 md:flex-row md:gap-1'>
        {/* RIGHT CONTENT */}
        <Container className='relative'>
          <Container className='mb-[28px] flex items-center justify-start gap-2 md:mb-[35px] md:gap-2.5 md:pr-1'>
            <Container
              center
              className='h-10 w-10 rounded-full border-2 border-white bg-[linear-gradient(247.65deg,_#FFC107_-35.57%,_#FF9737_100%)] shadow-[0px_5px_20px_0px_#DB924E4D]'
            >
              <Image
                src={imagesList.images.taskSquare}
                alt='step image'
                width={20}
                height={20}
                priority
              />
            </Container>
            <Text className='md:font-700! font-600! text-[16px] tracking-tighter! text-[#424B57]'>
              {productLocalization.takeYourBusinessOnlineIn5Steps}
            </Text>
          </Container>

          <Container className='relative h-[260px] pr-2.5 md:pr-1'>
            <ul className=''>
              {steps.map((step, index) => {
                const isActive = step.id === activeStep;
                const isDone = activeStep !== null && step.id <= activeStep;

                return (
                  <li key={step.id} className='relative flex gap-4'>
                    {/* DOT + LINE */}
                    <Container className='flex flex-col items-center'>
                      {/* DOT */}
                      <span
                        className={clsx(
                          'flex h-5 w-5 items-center justify-center rounded-full pt-0.5 text-sm text-[#fff] transition-colors duration-900',
                          isDone ? 'bg-[#FFAE11]' : 'bg-[#9F9F9F]',
                        )}
                      >
                        {step.id}
                      </span>

                      {/* LINE */}
                      {step.id !== steps.length && (
                        <span
                          className={clsx(
                            'w-0.5 transition-[height,background-color] duration-900 ease-in-out',
                            isActive || (activeStep && step.id < activeStep)
                              ? 'h-[31px] bg-[#FFAE11]'
                              : 'h-[31px] bg-[#FFAE1126]',
                            step.id === activeStep && 'h-[92px]! bg-[#FFAE1126]! md:h-[70px]!',
                          )}
                        />
                      )}
                    </Container>

                    {/* TEXT */}
                    <Container className={clsx(isActive && '-mt-1! pt-1 md:pt-0')}>
                      <p
                        className={clsx(
                          'w-fit cursor-pointer transition duration-300 hover:text-[#FFAE11]',
                          isActive
                            ? 'font-600! text-[16px] text-[#FF7B01] md:text-lg'
                            : 'text-[16px] font-bold! text-[#6F6F6F]',
                        )}
                        onClick={() => {
                          setActiveStep((prev) => {
                            if (prev === step.id) {
                              return null;
                            }

                            setVisibleStep(step.id);
                            setHasShownStepImage(true);
                            return step.id;
                          });
                        }}
                      >
                        {step.title}
                      </p>

                      <div
                        ref={(el) => {
                          contentRefs.current[index] = el;
                        }}
                        style={{
                          height: isActive ? getContentHeight(index) : 0,
                        }}
                        className='overflow-hidden transition-[height] duration-900 ease-in-out'
                      >
                        <p className='mt-2 text-sm leading-6 font-medium text-[#5B5C60] md:text-[16px]'>
                          {step.description}
                        </p>
                      </div>
                    </Container>
                  </li>
                );
              })}
            </ul>
          </Container>
        </Container>

        {/* LEFT IMAGE */}
        <Container className='relative h-[165px] w-[207px] md:h-[260px] md:w-[220px]'>
          {/* STEP IMAGES */}
          {steps.map((step) => {
            const isVisible = step.id === visibleStep;

            return (
              <Image
                key={step.id}
                src={step.image}
                alt='step image'
                width={220}
                height={192}
                priority
                className={clsx(
                  'absolute top-[16%] left-0 transition-opacity duration-900 ease-in-out',
                  isVisible ? 'opacity-100' : 'opacity-0',
                )}
              />
            );
          })}
        </Container>
      </Container>
    </Container>
  );
}
